import { FC, useState, useEffect } from 'react'
import {
  Spinner,
  Typography,
  Button,
  Card,
  CardBody,
  IconButton,
  Input,
  Switch,
  Tooltip,
  Alert,
} from '@material-tailwind/react'
import { PiX, PiInfoLight } from 'react-icons/pi'
import { useTranslation, Trans } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { logger } from '../utils/helpers/logger'
import { useFeatureAuth, useFeatureFlag } from '../hooks/authorization'
import { FEATURE_FLAGS, FEATURES } from '../utils/constants/features.constants'
import Divider from '../components/Divider'
import { useDeepCompareObjectsEffect } from '../hooks/toolkit'
import { updateExperienceDetails } from '../utils/helpers/upload.helpers'
import { validateInputNumber } from '../utils/helpers/validate.helpers'
import { useGeneralToast, useInteractiveToast } from '../hooks/toast'
import { useFormData } from '../hooks/form'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiExperience } from '../types/supabase'
import ShareExperiencePanel from './ShareExperiencePanel'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'

interface SaveSettingsToastProps {
  loading: boolean
  title: string
  confirm: string
}

const SaveSettingsToast: FC<SaveSettingsToastProps> = ({ loading, title, confirm }) => (
  <div className="w-full h-full flex flex-col justify-center items-center">
    {title}
    <Button
      type="submit"
      size="sm"
      disabled={loading}
      form="edit-experience-settings-form"
      className="bg-accent text-primary w-full mt-2"
    >
      {loading ? <Spinner className="h-4 w-4 mx-auto" /> : confirm}
    </Button>
  </div>
)

interface ExperienceSidebarProps {
  experienceDetails: AgamiExperience
  refetchExperienceDetails: () => void
  closeSidebar: () => void
  sidebarIsClosed: boolean
  isInBottomSheet?: boolean
}

export const ExperienceSidebar: FC<ExperienceSidebarProps> = ({
  experienceDetails,
  refetchExperienceDetails,
  closeSidebar,
  sidebarIsClosed,
  isInBottomSheet = false,
}) => {
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [loading, setLoading] = useState(false)
  const { formData, setFormData, handleDataChange, checkInputIsValid } = useFormData(experienceDetails)
  const [changedData] = useDeepCompareObjectsEffect(experienceDetails, formData)
  const experimentalFeats = useFeatureAuth(FEATURES.EXPERIMENTAL_FEATURES)
  const nfcFlag = useFeatureFlag(FEATURE_FLAGS.NFC_TRIGGER)
  const qrCodeFlag = useFeatureFlag(FEATURE_FLAGS.QR_TRIGGER)
  const { darkMode } = useDarkMode()
  const { t } = useTranslation()

  const { open, update, dismiss } = useInteractiveToast()

  const title = t('dashboardExperienceDetail.toastSettingsUpdated')
  const confirm = t('dashboardExperienceDetail.toastSaveChanges')

  useEffect(() => {
    if (changedData) {
      open(<SaveSettingsToast title={title} confirm={confirm} loading={loading} />)
    } else {
      dismiss()
    }
    return () => {
      dismiss()
    }
  }, [changedData])

  useEffect(() => {
    // @ts-expect-error toast library have incomplete types
    update({ render: () => <SaveSettingsToast loading={loading} title={title} confirm={confirm} /> })
  }, [loading, title, confirm])

  // reset form when sidebar is closed
  useEffect(() => {
    if (sidebarIsClosed) {
      setFormData(experienceDetails)
    }
  }, [sidebarIsClosed])

  const submitChanges = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setLoading(true)

    const {
      id,
      default_radius,
      default_deadband,
      fade_in_time,
      fade_out_time,
      accuracy,
      spots_replay,
      spots_overlap,
      enable_high_accuracy,
      autoplay_sounds,
      nfc_trigger,
      qr_trigger,
      light_onboarding,
    } = formData

    const { error } = await updateExperienceDetails({
      expNewData: {
        id,
        default_radius,
        default_deadband,
        fade_in_time,
        fade_out_time,
        accuracy,
        spots_replay,
        spots_overlap,
        enable_high_accuracy,
        autoplay_sounds,
        nfc_trigger,
        qr_trigger,
        light_onboarding,
      },
    })

    if (error) {
      toastError(t('dashboardExperienceDetail.errorGeneric'))
      logger.error(error, 'UpdateExperienceSettings')
      setLoading(false)
      return
    }

    await refetchExperienceDetails()
    setLoading(false)
    toastSuccess(t('dashboardExperienceDetail.successDetailsUpdated'))
  }

  return (
    <Card className="w-full" data-testid="experience-settings-panel">
      <CardBody className="w-full">
        {!isInBottomSheet && (
          <div className="absolute px-4 top-2 right-0">
            <IconButton size="sm" variant="text" color={darkMode ? 'white' : 'gray'} onClick={closeSidebar}>
              <PiX className="h-5 w-5 text-primary dark:text-base-gray-500" />
            </IconButton>
          </div>
        )}

        <div className="mb-10">
          {!isInBottomSheet && (
            <Typography variant="h5">{t('dashboardExperienceDetail.settingsTitle')}</Typography>
          )}

          <form
            id="edit-experience-settings-form"
            className="flex flex-col gap-3 my-4"
            onSubmit={submitChanges}
          >
            <Input
              type="number"
              label={t('dashboardExperienceDetail.radiusLabel')}
              min={0}
              value={formData.default_radius ?? ''}
              onChange={(e) =>
                handleDataChange('default_radius', Number(validateInputNumber(e.currentTarget.value ?? '')))
              }
              onBlur={() => checkInputIsValid('default_radius', (formData.default_radius ?? 0) > 0)}
              data-testid="experience-default-radius-input"
              {...(darkMode && { color: 'white' })}
            />

            <Input
              type="number"
              label={t('dashboardExperienceDetail.deadbandLabel')}
              min={0}
              value={formData.default_deadband ?? ''}
              onChange={(e) =>
                handleDataChange('default_deadband', Number(validateInputNumber(e.currentTarget.value ?? '')))
              }
              onBlur={() => checkInputIsValid('default_deadband', (formData.default_deadband ?? 0) > 0)}
              data-testid="experience-default-deadband-input"
              {...(darkMode && { color: 'white' })}
            />

            <Input
              type="number"
              label={t('dashboardExperienceDetail.fadeInLabel')}
              min={0}
              value={formData.fade_in_time ?? ''}
              onChange={(e) =>
                handleDataChange('fade_in_time', Number(validateInputNumber(e.currentTarget.value ?? '')))
              }
              onBlur={() => checkInputIsValid('fade_in_time', (formData.fade_in_time ?? 0) >= 0)}
              data-testid="experience-fade-in-input"
              {...(darkMode && { color: 'white' })}
            />

            <Input
              type="number"
              label={t('dashboardExperienceDetail.fadeOutLabel')}
              value={formData.fade_out_time ?? ''}
              min={0}
              onChange={(e) =>
                handleDataChange('fade_out_time', Number(validateInputNumber(e.currentTarget.value ?? '')))
              }
              onBlur={() => checkInputIsValid('fade_out_time', (formData.fade_out_time ?? 0) >= 0)}
              data-testid="experience-fade-out-input"
              {...(darkMode && { color: 'white' })}
            />

            <Input
              type="number"
              label={t('dashboardExperienceDetail.accuracyLabel')}
              min={1}
              value={formData.accuracy ?? ''}
              onChange={(e) =>
                handleDataChange('accuracy', Number(validateInputNumber(e.currentTarget.value ?? '')))
              }
              onBlur={() => checkInputIsValid('accuracy', (formData.accuracy ?? 0) > 0)}
              data-testid="experience-accuracy-input"
              {...(darkMode && { color: 'white' })}
            />

            <div className="flex justify-start items-center gap-1">
              <Switch
                color={(darkMode ? 'accent' : 'primary') as colors}
                id="spots_replay_switch"
                label={t('dashboardExperienceDetail.replayLabel')}
                checked={Boolean(formData.spots_replay)}
                onChange={(e) => handleDataChange('spots_replay', e.currentTarget.checked)}
                data-testid="experience-spots-replay-switch"
              />
              <Tooltip
                content={
                  <div className="w-[300px]">
                    <Typography variant="small" className="font-bold">
                      {t('dashboardExperienceDetail.replayHelp')}
                    </Typography>
                  </div>
                }
                placement="right"
                interactive
              >
                <span>
                  <PiInfoLight className="text-primary dark:text-base-gray-400" />
                </span>
              </Tooltip>
            </div>

            <div className="flex justify-start items-center gap-1">
              <Switch
                color={(darkMode ? 'accent' : 'primary') as colors}
                id="spots_overlap_switch"
                label={t('dashboardExperienceDetail.overlapLabel')}
                checked={Boolean(formData.spots_overlap)}
                onChange={(e) => handleDataChange('spots_overlap', e.currentTarget.checked)}
                data-testid="experience-spots-overlap-switch"
              />
              <Tooltip
                content={
                  <div className="w-[300px]">
                    <Typography variant="small" className="font-bold">
                      {t('dashboardExperienceDetail.overlapHelp')}
                    </Typography>
                  </div>
                }
                placement="right"
                interactive
              >
                <span>
                  <PiInfoLight className="text-primary dark:text-base-gray-400" />
                </span>
              </Tooltip>
            </div>

            <div className="flex justify-start items-center gap-1">
              <Switch
                color={(darkMode ? 'accent' : 'primary') as colors}
                id="light_onboarding"
                label={t('dashboardExperienceDetail.lightOnboardingLabel')}
                checked={Boolean(formData.light_onboarding)}
                onChange={(e) => handleDataChange('light_onboarding', e.currentTarget.checked)}
                data-testid="experience-light-onboarding-switch"
              />
              <Tooltip
                content={
                  <div className="w-[300px]">
                    <Typography variant="small" className="font-bold">
                      {t('dashboardExperienceDetail.lightOnboardingHelp')}
                    </Typography>
                  </div>
                }
                placement="right"
                interactive
              >
                <span>
                  <PiInfoLight className="text-primary dark:text-base-gray-400" />
                </span>
              </Tooltip>
            </div>

            <Divider color="dark" opacity={0.3} />

            <Typography className="font-bold">{t('dashboardExperienceDetail.systemTitle')}</Typography>

            {experimentalFeats && (
              <div className="flex justify-start items-center gap-1">
                <Switch
                  color={(darkMode ? 'accent' : 'primary') as colors}
                  id="high_accuracy_switch"
                  label={t('dashboardExperienceDetail.highAccuracyLabel')}
                  checked={Boolean(formData.enable_high_accuracy)}
                  onChange={(e) => handleDataChange('enable_high_accuracy', e.currentTarget.checked)}
                />
                <Tooltip
                  content={
                    <div className="w-[300px]">
                      <Typography variant="small" className="font-bold">
                        {t('dashboardExperienceDetail.highAccuracyHelp')}
                      </Typography>
                    </div>
                  }
                  placement="right"
                  interactive
                >
                  <span>
                    <PiInfoLight className="text-primary dark:text-base-gray-400" />
                  </span>
                </Tooltip>
              </div>
            )}

            <div className="flex justify-start items-center gap-1">
              <Switch
                color={(darkMode ? 'accent' : 'primary') as colors}
                id="autoplay_sounds_switch"
                label={t('dashboardExperienceDetail.autoplayLabel')}
                checked={Boolean(formData.autoplay_sounds)}
                onChange={(e) => handleDataChange('autoplay_sounds', e.currentTarget.checked)}
                data-testid="experience-autoplay-switch"
              />
              <Tooltip
                content={
                  <div className="w-[300px]">
                    <Typography variant="small" className="font-bold">
                      <Trans i18nKey="dashboardExperienceDetail.autoplayHelp">
                        {/* fallback text unused; using key */}
                      </Trans>
                    </Typography>
                  </div>
                }
                placement="right"
                interactive
              >
                <span>
                  <PiInfoLight className="text-primary dark:text-base-gray-400" />
                </span>
              </Tooltip>
            </div>

            {formData.autoplay_sounds && (
              <Alert
                variant={darkMode ? 'outlined' : 'ghost'}
                color="orange"
                data-testid="experience-autoplay-note"
              >
                <small>{t('dashboardExperienceDetail.autoplayIosNote')}</small>
              </Alert>
            )}

            {(nfcFlag || qrCodeFlag) && (
              <>
                <Divider color="dark" opacity={0.3} />

                <Typography className="font-bold">
                  {t('dashboardExperienceDetail.manualTriggerTitle')}
                </Typography>

                {nfcFlag && (
                  <div className="flex justify-start items-center gap-1">
                    <Switch
                      color={(darkMode ? 'accent' : 'primary') as colors}
                      id="nfc_trigger"
                      label={t('dashboardExperienceDetail.nfcTriggerLabel')}
                      checked={Boolean(formData.nfc_trigger)}
                      onChange={(e) => handleDataChange('nfc_trigger', e.currentTarget.checked)}
                    />
                    <Tooltip
                      content={
                        <div className="w-[300px]">
                          <Typography variant="small" className="font-bold">
                            {t('dashboardExperienceDetail.nfcTriggerHelp')}
                          </Typography>
                        </div>
                      }
                      placement="right"
                      interactive
                    >
                      <span>
                        <PiInfoLight className="text-primary dark:text-base-gray-400" />
                      </span>
                    </Tooltip>
                  </div>
                )}

                {qrCodeFlag && (
                  <div className="flex justify-start items-center gap-1">
                    <Switch
                      color={(darkMode ? 'accent' : 'primary') as colors}
                      id="qr_trigger"
                      label={t('dashboardExperienceDetail.qrTriggerLabel')}
                      checked={Boolean(formData.qr_trigger)}
                      onChange={(e) => handleDataChange('qr_trigger', e.currentTarget.checked)}
                    />
                    <Tooltip
                      content={
                        <div className="w-[300px]">
                          <Typography variant="small" className="font-bold">
                            {t('dashboardExperienceDetail.qrTriggerHelp')}
                          </Typography>
                        </div>
                      }
                      placement="right"
                      interactive
                    >
                      <span>
                        <PiInfoLight className="text-primary dark:text-base-gray-400" />
                      </span>
                    </Tooltip>
                  </div>
                )}
              </>
            )}
          </form>
        </div>

        <Divider color="dark" opacity={0.3} />

        {/* sharing */}
        <div className="mt-6">
          {!sidebarIsClosed && (
            <ShareExperiencePanel experience={experienceDetails} onSuccess={refetchExperienceDetails} />
          )}
        </div>

        <Divider color="dark" />

        <Typography>
          {t('dashboardExperienceDetail.createdAt', {
            date: formatDateTimeString(formData.created_at ?? ''),
          })}
        </Typography>
      </CardBody>
    </Card>
  )
}
